import { OAuthService } from 'angular-oauth2-oidc';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '@environment/environment';
import { TranslateService } from '@ngx-translate/core';
import { Departments, Jsons } from '@app/configs/app.config';
import { CryptoService } from './crypto.service';
import { DepartmentContextService } from './department-context.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitializationService {
  private appConfig!: typeof environment;

  /* istanbul ignore next */
  constructor(
    private readonly httpService: HttpClient,
    private readonly cryptoService: CryptoService,
    private readonly oauthService: OAuthService,
    private readonly departmentContext: DepartmentContextService,
    private readonly translateService: TranslateService,
  ) {
    // Following code is added so that unit test cases does not fail when api base path in appConfig is null
    if (!environment.production) {
      this.appConfig = environment;
    }
  }

  /**
   * Method to load the config.json.
   */
  /* istanbul ignore next */
  getSettings(): Promise<any> {
    return this.loadConfig({ shouldInitAuth: true });
  }

  /**
   * Common method to configure OAuth service
   * based on the provided authentication configuration.
   */
  /* istanbul ignore next */
  private configureOAuth(authConfig: any): void {
    this.oauthService.configure({
      issuer: authConfig.issuer,
      redirectUri: window.location.origin + authConfig.redirectUri,
      postLogoutRedirectUri:
        window.location.origin + authConfig.postLogoutRedirectUri,
      clientId: authConfig.clientId,
      responseType: authConfig.responseType,
      scope: authConfig.scope,
      showDebugInformation: !environment.production,
      strictDiscoveryDocumentValidation: false,
      oidc: true,
      requestAccessToken: true,
      sessionChecksEnabled: true,
    });
  }

  /**
   * Method to load the config based on department.
   */
  /* istanbul ignore next */
  loadConfigForDepartment(department: string): Promise<any> {
    return this.loadConfig({ department });
  }

  /**
   * Method to load the config based on department and initialize auth if required.
   */
  /* istanbul ignore next */
  private async loadConfig(
    options: { shouldInitAuth?: boolean; department?: string } = {},
  ): Promise<any> {
    const { shouldInitAuth = false, department } = options;

    const settings = await lastValueFrom(
      this.httpService.get('assets/config/config.json'),
    );
    this.appConfig = this.decryptConfigurationValues(settings);
    environment.auth = this.appConfig.auth;
    environment.APIBasePath = this.appConfig.APIBasePath;
    const origin = window.location.origin;
    const isScienceReady = department
      ? department === Departments.SCIENCE_READY
      : origin === environment.auth.scienceReady.scienceReadyUrl;
    const activeDepartment = isScienceReady
      ? Departments.SCIENCE_READY
      : Departments.SITE_READY;

    const authConfig = isScienceReady
      ? this.appConfig.auth.scienceReady
      : this.appConfig.auth.siteReady;
    if (shouldInitAuth) {
      this.configureOAuth(authConfig);
    }

    this.setTranslationForDepartment(activeDepartment);
    this.departmentContext.setActiveDepartment(activeDepartment);
    return this.appConfig;
  }

  /**
   * Method to set translation based on department.
   */
  /* istanbul ignore next */
  setTranslationForDepartment(department: string): void {
    const lang =
      department === Departments.SCIENCE_READY
        ? Jsons.SCIENCE_READY_JSON
        : Jsons.SITE_READY_JSON;
    this.translateService.setDefaultLang(Jsons.SCIENCE_READY_JSON);
    this.translateService.use(lang);
  }

  /**
   * Method to decrypt configuration values
   */
  /* istanbul ignore next */
  decryptConfigurationValues(settings: any): any {
    Object.keys(settings).forEach((key) => {
      const value = settings[key];
      // const shouldDecrypt = ['issuer', 'clientId', 'scope'].includes(key);
      if (typeof value === 'string') {
        // const decryptedValue = this.cryptoService.decrypt(value);
        settings[key] = value;
      } else if (typeof value === 'object' && value !== null) {
        this.decryptConfigurationValues(value);
      }
    });

    return settings;
  }
}
